/**
 * 智能体节点：LLM 规划 + 批量生图的两段式编排卡。
 *
 *  - 卡片内：指令输入、模型与参数（弹层）、「① 规划」按钮；
 *    规划产出后展示可编辑计划（统一风格前缀 + 各图差异提示词/标签），
 *    「② 开始生成」按计划建生图工作卡与图片组并批量执行；
 *    部分失败可单独重试失败项。
 *  - 下方面板（选中时展开）：各图完整提示词预览。
 *
 * 一致性（风格圣经）：每张最终提示词 = stylePrefix + 该张差异描述，
 * 由 agentGenerator.composeAgentPrompt 拼接，节点与工作卡共用同一口径。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bot,
  ChevronDown,
  Loader2,
  Plus,
  RotateCcw,
  RotateCw,
  Wand2,
  X,
} from 'lucide-react';
import type { CanvasNode, AgentNodeData, AgentPlanItem, ImageGroupNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeParamsPopover } from './NodeParamsPopover';
import { boardActions, getBoardState, useBoard } from '../state/boardStore';
import { aggregateUpstreamReferenceImages, buildUpstreamSignature } from '../state/upstream';
import { getCachedImageModels } from '../modelCache';
import { writeNodeParamMemory } from '../utils/nodeParamMemory';
import { validateImageReferenceQuota } from '../utils/referenceImageQuota';
import type { ModelConfig } from '../../../services/api';
import toast from '../../../components/Toast';
import {
  runAgentPlanning,
  runAgentGeneration,
  retryAgentItems,
  composeAgentPrompt,
  MAX_AGENT_ITEMS,
} from '../generators/agentGenerator';
import { ensureAgentContainer, fitAgentContainer } from '../generators/agentUnit';
import { removeImageGroupSlot } from '../generators/imageGenerator';
import { ConfirmDialog, type ConfirmDialogState } from '../components/dialogs/ConfirmDialog';

interface AgentNodeProps {
  node: CanvasNode<AgentNodeData>;
  selected: boolean;
  highlighted: boolean;
}

const ASPECT_OPTIONS = ['auto', '1:1', '16:9', '9:16', '4:3', '3:4'];
const QUALITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'low', label: '标准' },
  { value: 'medium', label: '高清' },
  { value: 'high', label: '超清' },
];

/** 计划在非生成中均可编辑：执行完成后允许改计划（或原样）重新生成，重生成前会确认放弃上一轮产物 */
function isPlanEditable(data: AgentNodeData): boolean {
  return !data.generating;
}

export function AgentNode({ node, selected, highlighted }: AgentNodeProps) {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [paramsOpen, setParamsOpen] = useState(false);
  const paramsRef = useRef<HTMLDivElement | null>(null);
  // 重新生成前的放弃确认（弹窗 portal 到 body，避开画布 transform 祖先）
  const [confirmState, setConfirmState] = useState<ConfirmDialogState>({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  const data = node.data;
  const status = data.status || 'idle';
  const items = data.items ?? [];
  const itemStatuses = data.itemStatuses ?? [];
  const generating = !!data.generating;
  const planEditable = isPlanEditable(data);
  // 上一轮产物（工作卡/图片组）是否仍在画布上：决定按钮文案与"放弃"确认（点击时以实时状态为准）
  const hasPreviousBatch = (() => {
    const ids = [...(data.childNodeIds ?? []), ...(data.groupNodeId ? [data.groupNodeId] : [])];
    if (ids.length === 0) return false;
    const existing = new Set(getBoardState().nodes.map((n) => n.id));
    return ids.some((id) => existing.has(id));
  })();
  const completedCount = itemStatuses.filter((s) => s === 'completed').length;
  const failedCount = itemStatuses.filter((s) => s === 'failed').length;

  // 刷新恢复：订阅组节点终态，agent 仍 executing 时按组内格子状态收敛自身
  const groupNodeId = data.groupNodeId ?? null;
  const groupStatus = useBoard((s) =>
    groupNodeId
      ? (s.nodes.find((n) => n.id === groupNodeId)?.data as ImageGroupNodeData | undefined)?.status
      : undefined
  );
  const readOnly = useBoard((s) => s.readOnly);
  useEffect(() => {
    if (readOnly) return;
    if (!generating || !groupNodeId) return;
    // 组节点已被删除（执行中撤销）：无从恢复，直接收敛为失败
    if (!getBoardState().nodes.some((n) => n.id === groupNodeId)) {
      boardActions.updateNodeData(node.id, {
        generating: false,
        status: 'failed',
        errorMessage: '图片组已被删除，批量任务中断',
      });
      return;
    }
    if (groupStatus !== 'completed' && groupStatus !== 'failed') return;
    const group = getBoardState().nodes.find((n) => n.id === groupNodeId)?.data as
      | ImageGroupNodeData
      | undefined;
    const statuses = group?.statuses ?? [];
    const final = statuses.length > 0 ? statuses : itemStatuses;
    const completed = final.filter((s) => s === 'completed').length;
    boardActions.updateNodeData(node.id, {
      generating: false,
      ...(statuses.length > 0 ? { itemStatuses: [...statuses] } : {}),
      status: completed === 0 ? 'failed' : completed === final.length ? 'done' : 'partial_failed',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, groupNodeId, groupStatus, node.id]);

  // 冻结外框自愈（挂载时一次）：存量批次无外框则补建；外框按实际成员尺寸校正
  // （建批时按默认尺寸估算，结果组随图片填充会增高）。fit 短路保证落库后不再产生变更
  useEffect(() => {
    if (readOnly) return;
    if (data.generating) return;
    const existing = new Set(getBoardState().nodes.map((n) => n.id));
    const hasBatch =
      (data.childNodeIds ?? []).some((id) => existing.has(id)) ||
      (data.groupNodeId ? existing.has(data.groupNodeId) : false);
    if (hasBatch) ensureAgentContainer(node.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, readOnly]);

  const selectedModelConfig = useMemo(
    () => models.find((m) => m.id === data.modelId),
    [models, data.modelId]
  );

  useEffect(() => {
    let cancelled = false;
    getCachedImageModels()
      .then((list) => {
        if (cancelled) return;
        setModels(list);
        if (readOnly) return;
        if (list.length > 0 && (!node.data.modelId || !list.some((m) => m.id === node.data.modelId))) {
          // 未选模型，或参数记忆/历史数据里的模型已下线或被隐藏：
          // 回退到首个可用模型（派生校正，静默写入不触发自动保存）
          boardActions.updateNodeDataSilent(node.id, {
            modelId: list[0].id,
            aspectRatio: node.data.aspectRatio ?? 'auto',
            quality: node.data.quality ?? 'low',
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (patch: Partial<AgentNodeData>) => {
    boardActions.updateNodeData(node.id, patch as Record<string, unknown>);
    // 记住用户的参数偏好（模型 / 比例 / 清晰度 / 参考图链），下次新建智能体自动套用
    writeNodeParamMemory('agent', patch);
  };

  const updateItem = (index: number, patch: Partial<AgentPlanItem>) => {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange({ items: next });
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    // 三方同步：计划项、对应生图工作卡（childNodeIds[index]）、图片组槽位。
    // 只过滤 items/itemStatuses 会让三组数组从此索引错位，事后重试失败项时
    // 会把新任务写进被删项的卡片/槽位
    if (data.groupNodeId) {
      removeImageGroupSlot(data.groupNodeId, index);
    }
    const removedCardId = data.childNodeIds?.[index];
    if (removedCardId && getBoardState().nodes.some((n) => n.id === removedCardId)) {
      boardActions.removeNodes([removedCardId]);
    }
    onChange({
      items: items.filter((_, i) => i !== index),
      itemStatuses: itemStatuses.filter((_, i) => i !== index),
      childNodeIds: (data.childNodeIds ?? []).filter((_, i) => i !== index),
    });
    // 冻结外框随存活成员收缩（无外框的存量批次为空操作）
    fitAgentContainer(node.id);
  };

  const addItem = () => {
    if (items.length >= MAX_AGENT_ITEMS) return;
    onChange({
      items: [...items, { label: `图片 ${items.length + 1}`, prompt: '' }],
      itemStatuses: [...itemStatuses, 'pending'],
    });
  };

  const handlePlan = async () => {
    if (generating) return;
    await runAgentPlanning(node.id);
  };

  const handleGenerate = async () => {
    if (generating) return;
    if (items.filter((it) => it.prompt.trim()).length === 0) {
      toast.error('计划中没有有效提示词');
      return;
    }
    // 已有上一轮产物：重新生成前确认放弃——删除上一批工作卡、结果图片组与
    // 冻结外框（已从组内取出的图片是用户显式拿走的，不在删除范围）
    const existing = new Set(getBoardState().nodes.map((n) => n.id));
    const prevCardIds = (data.childNodeIds ?? []).filter((id) => existing.has(id));
    const prevGroupIds =
      data.groupNodeId && existing.has(data.groupNodeId) ? [data.groupNodeId] : [];
    const prevContainerId =
      data.containerNodeId && existing.has(data.containerNodeId) ? [data.containerNodeId] : [];
    if (prevCardIds.length > 0 || prevGroupIds.length > 0 || prevContainerId.length > 0) {
      const parts: string[] = [];
      if (prevCardIds.length > 0) parts.push(`${prevCardIds.length} 张生图工作卡`);
      if (prevGroupIds.length > 0) parts.push('结果图片组');
      setConfirmState({
        open: true,
        title: '放弃上一轮产物？',
        message: `重新生成将删除上一轮的${parts.join('与')}，已从组内取出的图片会保留。`,
        onConfirm: () => {
          setConfirmState((s) => ({ ...s, open: false }));
          boardActions.removeNodes([...prevCardIds, ...prevGroupIds, ...prevContainerId]);
          void runAgentGeneration(node.id);
        },
      });
      return;
    }
    await runAgentGeneration(node.id);
  };

  const handleRetryFailed = async () => {
    if (generating) return;
    await retryAgentItems(node.id);
  };

  const handleReplan = () => {
    if (generating) return;
    onChange({ status: 'idle', errorMessage: null });
  };

  const availableQualityOptions = useMemo(
    () =>
      QUALITY_OPTIONS.filter((option) => {
        if (!selectedModelConfig) return true;
        if (option.value === 'low') return selectedModelConfig.enableStandardSize !== false;
        if (option.value === 'medium') return selectedModelConfig.enableHdSize !== false;
        if (option.value === 'high') return selectedModelConfig.enableUhdSize !== false;
        return true;
      }),
    [selectedModelConfig]
  );
  const qualityLabel =
    QUALITY_OPTIONS.find((q) => q.value === (data.quality || 'low'))?.label ?? '标准';

  // 参考图链可用性：模型需支持"带 1 张参考图的图生图"；模型信息未加载时乐观放行
  const chainAvailable = useMemo(
    () =>
      !selectedModelConfig ||
      (selectedModelConfig.enableImg2img !== false &&
        validateImageReferenceQuota('img2img', 1, selectedModelConfig.modeCapabilities) === null),
    [selectedModelConfig]
  );
  const chainOn = (data.useChainReference ?? true) && chainAvailable;

  // 上游参考图（image / image_group 逐张 / video 封面）：只订阅上游签名，内容不变不重算
  const upstreamSig = useBoard((s) => buildUpstreamSignature(node.id, s.nodes, s.edges));
  const upstreamRefCount = useMemo(() => {
    const { nodes: curNodes, edges: curEdges } = getBoardState();
    return aggregateUpstreamReferenceImages(node.id, curNodes, curEdges).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upstreamSig, node.id]);

  // ---------------- 下方面板 ----------------
  const panel = (
    <div className="space-y-2">
      <div>
        <label className="mb-1 text-[10px] font-medium text-slate-400">完整提示词预览（风格前缀 + 各图差异）</label>
        {items.length === 0 ? (
          <p className="text-[10px] text-slate-300">规划后此处展示每张图的最终提示词</p>
        ) : (
          <div className="space-y-1">
            {items.map((item, i) => (
              <div key={i} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
                <span className="mr-1 text-[10px] font-medium text-slate-400">{i + 1}. {item.label}</span>
                <p className="text-[10px] leading-relaxed text-slate-600">
                  {composeAgentPrompt(data.stylePrefix || '', item.prompt)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ---------------- 卡片 ----------------
  return (
    <NodeWrapper node={node} selected={selected} highlighted={highlighted} panel={panel} stretchContent>
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 text-[11px]">
        {/* 指令（flex-1：卡片手动放大时输入区撑满剩余高度，自然高度不小于 3.5rem） */}
        <textarea
          value={data.instruction || ''}
          onChange={(e) => onChange({ instruction: e.target.value })}
          readOnly={generating}
          placeholder="告诉智能体要生成什么，如：生成3张同一角色不同角度的图片，角色是……"
          className="min-h-[3.5rem] w-full flex-1 resize-none rounded-md border border-light-border bg-white p-2 text-[11px] outline-none focus:border-blue-400"
        />

        {/* 模型 + 参数 */}
        <div className="flex gap-1.5">
          <select
            value={data.modelId || ''}
            onChange={(e) => onChange({ modelId: e.target.value })}
            className="min-w-0 flex-1 rounded-md border border-light-border bg-white px-2 py-1.5 text-xs"
            title="图片模型"
          >
            <option value="">选择图片模型</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <div className="relative" ref={paramsRef}>
            <button
              type="button"
              onClick={() => setParamsOpen((v) => !v)}
              className={`flex h-full items-center gap-1 rounded-md border px-2 text-[10px] font-medium transition ${
                paramsOpen
                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-light-border bg-white text-slate-600 hover:bg-slate-50'
              }`}
              title="比例 · 清晰度"
            >
              {data.aspectRatio || 'auto'} · {qualityLabel}
              <ChevronDown className="h-3 w-3" />
            </button>
            <NodeParamsPopover open={paramsOpen} onClose={() => setParamsOpen(false)} anchor={paramsRef} width={192}>
              <div>
                <label className="mb-0.5 block text-[10px] text-slate-400">比例</label>
                <select
                  value={data.aspectRatio || 'auto'}
                  onChange={(e) => onChange({ aspectRatio: e.target.value })}
                  className="w-full rounded-md border border-light-border bg-white px-2 py-1 text-xs"
                >
                  {ASPECT_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-slate-400">清晰度</label>
                <select
                  value={data.quality || 'low'}
                  onChange={(e) => onChange({ quality: e.target.value })}
                  className="w-full rounded-md border border-light-border bg-white px-2 py-1 text-xs"
                >
                  {availableQualityOptions.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
                </select>
              </div>
            </NodeParamsPopover>
          </div>
        </div>

        {/* 上游参考图提示 */}
        {upstreamRefCount > 0 && (
          <p
            className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] leading-relaxed text-emerald-700"
            title="上游图片（图片节点 / 图片组逐张 / 视频封面）会作为参考图注入每一张生成，包括基准图"
          >
            已连接 {upstreamRefCount} 张上游参考图，将注入每张生成（含基准图）
          </p>
        )}

        {/* 参考图链开关 */}
        <label
          className={`flex items-center gap-1.5 text-[10px] ${
            chainAvailable ? 'cursor-pointer text-slate-500' : 'cursor-not-allowed text-slate-300'
          }`}
          title={
            chainAvailable
              ? '第 1 项作为基准图先生成，其余各项携带基准图作为参考生成，显著提升人物/主体一致性'
              : '当前模型不支持图生图/参考图，已自动降级为纯文生图'
          }
        >
          <input
            type="checkbox"
            checked={data.useChainReference ?? true}
            disabled={!chainAvailable}
            onChange={(e) => onChange({ useChainReference: e.target.checked })}
            className="h-3 w-3 accent-blue-500"
          />
          首图作参考（基准图先行，提升一致性）
        </label>

        {/* ① 规划 */}
        <button
          type="button"
          onClick={handlePlan}
          disabled={generating}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-500 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'planning' ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>规划中...</span>
            </>
          ) : (
            <>
              <Bot className="h-3.5 w-3.5" />
              <span>{items.length > 0 ? '重新规划' : '① 规划'}</span>
            </>
          )}
        </button>

        {/* 计划审阅区（规划产出后） */}
        {items.length > 0 && (
          <div className="space-y-1.5 rounded-md border border-blue-100 bg-blue-50/40 p-1.5">
            <div>
              <label className="mb-0.5 block text-[10px] font-medium text-slate-400">
                统一风格（保证系列一致，注入每张提示词）
              </label>
              <textarea
                value={data.stylePrefix || ''}
                onChange={(e) => onChange({ stylePrefix: e.target.value })}
                readOnly={!planEditable}
                placeholder="主体/风格/场景/光影……"
                className="h-10 w-full resize-none rounded-md border border-light-border bg-white p-1.5 text-[10px] leading-relaxed outline-none focus:border-blue-400"
              />
            </div>
            {items.map((item, i) => {
              const itemStatus = itemStatuses[i];
              return (
                <div key={i} className="flex items-start gap-1">
                  <input
                    value={item.label}
                    onChange={(e) => updateItem(i, { label: e.target.value })}
                    readOnly={!planEditable}
                    className="w-16 shrink-0 rounded-md border border-light-border bg-white px-1.5 py-1 text-[10px] outline-none focus:border-blue-400"
                    title="标签"
                  />
                  {chainOn && i === 0 && (
                    <span
                      className="mt-1 shrink-0 rounded bg-indigo-100 px-1 py-0.5 text-[9px] font-medium text-indigo-600"
                      title="本项作为基准图先生成，其余各项以其为参考"
                    >
                      基准
                    </span>
                  )}
                  <textarea
                    value={item.prompt}
                    onChange={(e) => updateItem(i, { prompt: e.target.value })}
                    readOnly={!planEditable}
                    placeholder="该张独有的视角/构图差异描述"
                    className="h-9 min-w-0 flex-1 resize-none rounded-md border border-light-border bg-white p-1.5 text-[10px] leading-relaxed outline-none focus:border-blue-400"
                  />
                  <div className="flex shrink-0 items-center gap-0.5 pt-0.5">
                    {itemStatus === 'processing' && <Loader2 className="h-3 w-3 animate-spin text-blue-500" />}
                    {itemStatus === 'completed' && <span className="text-[10px] text-green-600">✓</span>}
                    {itemStatus === 'failed' && <span className="text-[10px] text-red-500" title="生成失败">✗</span>}
                    {/* 单项重生成：按当前提示词（含编辑后）重跑该张并回写结果组槽位 */}
                    {(itemStatus === 'completed' || itemStatus === 'failed') && (
                      <button
                        type="button"
                        onClick={() => void retryAgentItems(node.id, [i])}
                        disabled={generating || !data.groupNodeId || !(item.prompt || '').trim()}
                        className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-blue-500 disabled:opacity-30"
                        title={
                          !data.groupNodeId
                            ? '生成批次后可用'
                            : !(item.prompt || '').trim()
                              ? '该项提示词为空'
                              : '按当前提示词重新生成该张'
                        }
                      >
                        <RotateCw className="h-3 w-3" />
                      </button>
                    )}
                    {planEditable && (
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        disabled={items.length <= 1}
                        className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-red-500 disabled:opacity-30"
                        title={items.length <= 1 ? '至少保留一项' : '删除该项'}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {planEditable && items.length < MAX_AGENT_ITEMS && (
              <button
                type="button"
                onClick={addItem}
                className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 py-1 text-[10px] text-slate-400 transition hover:border-blue-300 hover:text-blue-500"
              >
                <Plus className="h-3 w-3" />
                添加一项（最多 {MAX_AGENT_ITEMS} 张）
              </button>
            )}
          </div>
        )}

        {/* ② 开始生成 / 重新生成 / 执行进度 */}
        {items.length > 0 && (
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !data.modelId || !planEditable}
            title={
              !data.modelId
                ? '请先选择图片模型'
                : hasPreviousBatch
                  ? '将删除上一轮工作卡与图片组后按当前计划重新生成'
                  : undefined
            }
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-500 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-purple-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === 'executing' ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>生成中 {completedCount}/{items.length}</span>
              </>
            ) : (
              <>
                <Wand2 className="h-3.5 w-3.5" />
                <span>
                  {hasPreviousBatch ? '重新生成' : '② 开始生成'}（{items.filter((it) => it.prompt.trim()).length} 张）
                </span>
              </>
            )}
          </button>
        )}

        {/* 失败项重试 / 重新规划 */}
        {!generating && failedCount > 0 && (
          <button
            type="button"
            onClick={handleRetryFailed}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-100 py-1.5 text-xs font-medium text-orange-700 transition hover:bg-orange-200"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>重试失败项（{failedCount}）</span>
          </button>
        )}
        {!generating && ['done', 'partial_failed', 'failed'].includes(status) && items.length > 0 && (
          <button
            type="button"
            onClick={handleReplan}
            className="w-full rounded-lg border border-light-border bg-white py-1 text-[10px] text-slate-500 transition hover:bg-slate-50"
          >
            重新规划新一批
          </button>
        )}

        {/* 状态与错误 */}
        {generating && status === 'executing' && (
          <p className="text-center text-[9px] text-slate-400">
            {data.chainApplied
              ? `基准图先行，其余带参考生成 · 已完成 ${completedCount}/${items.length}，失败可单独重试`
              : `已完成 ${completedCount}/${items.length}，失败可单独重试`}
          </p>
        )}
        {!generating && status === 'done' && (
          <p className="text-center text-[9px] text-green-600">全部完成（{items.length} 张）</p>
        )}
        {data.errorMessage && (
          <p className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] leading-relaxed text-red-600">
            {data.errorMessage}
          </p>
        )}
      </div>

      {/* 重新生成的放弃确认：portal 到 body（fixed 定位在画布 transform 祖先下会错位） */}
      {createPortal(
        <ConfirmDialog
          {...confirmState}
          onCancel={() => setConfirmState((s) => ({ ...s, open: false }))}
        />,
        document.body
      )}
    </NodeWrapper>
  );
}
